/**
 * EdgeDocs - Legal Document Editor
 * Main component for the EdgeDocs module with Phase 2 advanced features
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LexicalDocumentEditor } from './components/LexicalDocumentEditor';
import { VersionComparisonPanel } from './components/VersionComparisonPanel';
import { VersionApprovalWorkflow } from './components/VersionApprovalWorkflow';
import { VersionMetadataEditor } from './components/VersionMetadataEditor';
import { VersionRollbackModal } from './components/VersionRollbackModal';
import { useEditorStore } from './store/editorStore';
import { DocumentService } from '../../shared/document/DocumentService';
import { DocumentVersion } from '../../shared/document/types';
import { 
  DocumentTextIcon,
  EyeIcon,
  TagIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';


interface EdgeDocsProps {
  documentId?: string;
  panelMode?: boolean;
  readOnly?: boolean;
  onClose?: () => void;
}

type ActivePanel = 'editor' | 'comparison' | 'approval' | 'metadata';

/**
 * EdgeDocs Component
 * Handles document loading and editor lifecycle with Phase 2 features
 */
export function EdgeDocs({ 
  documentId: propDocumentId, 
  panelMode = false, 
  readOnly = false,
  onClose 
}: EdgeDocsProps): React.JSX.Element {
  
  console.log('🔄 [EdgeDocs] Rendered with:', {
    propDocumentId,
    panelMode,
    readOnly,
    timestamp: Date.now()
  });
  
  const params = useParams();
  const navigate = useNavigate();

  
  // Get document ID from props, params, or store
  const documentId = propDocumentId || params.documentId;
  
  // Validate document ID to prevent infinite loops
  const isValidDocumentId = documentId && 
    documentId.length > 0 && 
    documentId !== 'undefined' && 
    documentId !== 'null' &&
    documentId !== 'ef2ee9a9-9f75-4ddd-aeb9-e68c2609f2ce'; // Block the problematic ID
  
  // Editor store
  const {
    activeDocument,
    isEditorOpen,
    openEditor,
    closeEditor,
    loadDocumentContent,
    saveDocumentContent,
    setPanelMode,
    setReadOnly,
    editorState,
  } = useEditorStore();
  
  console.log('🔍 [EdgeDocs] Store values:', {
    activeDocumentId: activeDocument?.id,
    isEditorOpen,
    editorStateKeys: Object.keys(editorState),
    contentLength: editorState.content?.length,
    hasUnsavedChanges: editorState.hasUnsavedChanges
  });
  
  // Phase 2 state
  const [activePanel, setActivePanel] = useState<ActivePanel>('editor');
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [selectedVersions, setSelectedVersions] = useState<{
    left?: DocumentVersion;
    right?: DocumentVersion;
  }>({});
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<DocumentVersion | null>(null);
  const [currentVersion, setCurrentVersion] = useState<DocumentVersion | null>(null);
  
  // Use stable ref for initial content to prevent re-renders
  const stableInitialContentRef = useRef(editorState.content);
  
  // Only update the ref if content is significantly different (not just on every keystroke)
  useEffect(() => {
    if (editorState.content && editorState.content !== stableInitialContentRef.current) {
      // Only update if content has substantially changed (to avoid keystroke updates)
      const contentDiff = Math.abs(editorState.content.length - (stableInitialContentRef.current?.length || 0));
      if (contentDiff > 10 || !stableInitialContentRef.current) {
        console.log('🔄 [EdgeDocs] Updating stable content ref:', {
          oldLength: stableInitialContentRef.current?.length || 0,
          newLength: editorState.content.length,
          diff: contentDiff
        });
        stableInitialContentRef.current = editorState.content;
      }
    }
  }, [editorState.content]);
  
  // Load document from API
  const loadDocument = useCallback(async (id: string) => {
    try {
      // Check if we already have the document in the store
      if (activeDocument && activeDocument.id === id) {
        // Document already loaded, just ensure editor is open
        if (!isEditorOpen) {
          openEditor(activeDocument);
        }
        await loadDocumentContent(id);
        return;
      }
      
      // Load document from API
      const document = await DocumentService.getDocumentById(id);
      openEditor(document);
      await loadDocumentContent(id);
    } catch (error) {
      console.error('Failed to load document:', error);
      // Handle error - could show error message or redirect
    }
  }, [activeDocument, isEditorOpen, openEditor, loadDocumentContent]);
  
  // Load document when component mounts or documentId changes
  useEffect(() => {
    if (isValidDocumentId) {
      // If we have a documentId but no active document, or if the documentId changed
      if (!activeDocument || activeDocument.id !== documentId) {
        loadDocument(documentId);
      }
    }
  }, [documentId, isValidDocumentId, loadDocument]); // FIXED: Added proper dependencies with memoized loadDocument
  
  // Set panel mode and read-only state
  useEffect(() => {
    setPanelMode(panelMode);
    setReadOnly(readOnly);
  }, [panelMode, readOnly, setPanelMode, setReadOnly]);

  // Load versions when document changes
  useEffect(() => {
    if (isValidDocumentId) {
      loadVersions();
    }
  }, [documentId, isValidDocumentId]);

  // Load document versions
  const loadVersions = async () => {
    if (!documentId) return;
    
    try {
      const versionList = await DocumentService.getDocumentVersions(documentId);
      const sortedVersions = versionList.sort((a, b) => b.version - a.version);
      setVersions(sortedVersions);
      
      // Set current version (latest)
      if (sortedVersions.length > 0) {
        setCurrentVersion(sortedVersions[0]);
      }
    } catch (error) {
      console.error('Failed to load versions:', error);
    }
  };
  
  // Handle editor close
  const handleClose = () => {
    closeEditor();
    onClose?.();
    
    // Navigate back if in panel mode
    if (panelMode) {
      navigate(-1);
    }
  };
  
  // Handle document save
  const handleSave = async (content: string, metadata?: any) => {
    if (!activeDocument) return;
    
    try {
      await saveDocumentContent(content, metadata);
      console.log('Document saved successfully');
      
      // Reload versions after save
      await loadVersions();
    } catch (error) {
      console.error('Failed to save document:', error);
      // Handle error - could show error message
    }
  };

  // Handle version selection for comparison
  const handleVersionSelect = (version: DocumentVersion, side: 'left' | 'right') => {
    setSelectedVersions(prev => ({
      ...prev,
      [side]: version,
    }));
  };

  // Handle rollback
  const handleRollback = (version: DocumentVersion) => {
    setRollbackTarget(version);
    setShowRollbackModal(true);
  };

  // Handle rollback completion
  const handleRollbackComplete = async () => {
    await loadVersions();
    setShowRollbackModal(false);
    setRollbackTarget(null);
  };

  // Handle approval change
  const handleApprovalChange = async (_: any, newStatus: any) => {
    console.log('Approval status changed:', newStatus);
    // Reload versions to reflect changes
    await loadVersions();
  };

  // Handle metadata update
  const handleMetadataUpdate = async (metadata: any) => {
    console.log('Metadata updated:', metadata);
    // Reload versions to reflect changes
    await loadVersions();
  };
  
  // Show loading state if document is not loaded
  if (!activeDocument && documentId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading document...</span>
      </div>
    );
  }
  
  // Show error state if no document ID provided
  if (!documentId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No Document Selected
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Please select a document to edit.
          </p>
        </div>
      </div>
    );
  }

  // Panel mode: render as contained component with sidebar
  if (panelMode) {
    return (
      <div className="h-full flex bg-white dark:bg-gray-900">


        {/* Sidebar */}
        <div className="w-80 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              EdgeDocs
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {activeDocument?.title}
            </p>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-2">
            <button
              onClick={() => setActivePanel('editor')}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activePanel === 'editor'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              <DocumentTextIcon className="h-5 w-5" />
              <span>Editor</span>
            </button>

            <button
              onClick={() => setActivePanel('comparison')}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activePanel === 'comparison'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              <EyeIcon className="h-5 w-5" />
              <span>Version Comparison</span>
            </button>

            <button
              onClick={() => setActivePanel('approval')}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activePanel === 'approval'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              <ShieldCheckIcon className="h-5 w-5" />
              <span>Approval Workflow</span>
            </button>

            <button
              onClick={() => setActivePanel('metadata')}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activePanel === 'metadata'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              <TagIcon className="h-5 w-5" />
              <span>Metadata</span>
            </button>
          </nav>

          {/* Version History */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
              Version History
            </h3>
            <div className="space-y-2">
              {versions.map((version) => (
                <div
                  key={version.id}
                  className="p-2 bg-white dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Version {version.version}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(version.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {currentVersion?.id === version.id && (
                        <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded-full">
                          Current
                        </span>
                      )}
                      {currentVersion && currentVersion.id !== version.id && (
                        <button
                          onClick={() => handleRollback(version)}
                          className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                          title="Rollback to this version"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {activePanel === 'editor' && (
            <LexicalDocumentEditor
              document={activeDocument || undefined}
              isOpen={isEditorOpen}
              onClose={handleClose}
              onSave={handleSave}
              readOnly={readOnly}
              panelMode={true}
              autoSave={true}
              initialContent={stableInitialContentRef.current}
              showSidebar={true}
            />
          )}

          {activePanel === 'comparison' && (
            <VersionComparisonPanel
              documentId={documentId}
              leftVersion={selectedVersions.left}
              rightVersion={selectedVersions.right}
              onVersionSelect={handleVersionSelect}
              onClose={() => setActivePanel('editor')}
            />
          )}

          {activePanel === 'approval' && currentVersion && (
            <div className="p-6">
              <VersionApprovalWorkflow
                documentId={documentId}
                currentVersion={currentVersion}
                onApprovalChange={handleApprovalChange}
              />
            </div>
          )}

          {activePanel === 'metadata' && currentVersion && (
            <div className="p-6">
              <VersionMetadataEditor
                documentId={documentId}
                version={currentVersion}
                onMetadataUpdate={handleMetadataUpdate}
                onClose={() => setActivePanel('editor')}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Modal mode: render as full-screen overlay with editor only
  return (
    <>
      <div className="fixed inset-0 z-50 overflow-hidden bg-gray-900 bg-opacity-50">
        <div className="absolute inset-0 flex">
          {/* Main Editor Area */}
          <div className="flex-1 flex flex-col bg-white dark:bg-gray-900">
            
            <LexicalDocumentEditor
              document={activeDocument || undefined}
              isOpen={isEditorOpen}
              onClose={handleClose}
              onSave={handleSave}
              readOnly={readOnly}
              panelMode={false}
              autoSave={true}
              initialContent={stableInitialContentRef.current}
              showSidebar={true}
            />
          </div>
        </div>
      </div>

      {/* Rollback Modal */}
      {showRollbackModal && rollbackTarget && currentVersion && (
        <VersionRollbackModal
          isOpen={showRollbackModal}
          onClose={() => setShowRollbackModal(false)}
          documentId={documentId}
          targetVersion={rollbackTarget}
          currentVersion={currentVersion}
          onRollbackComplete={handleRollbackComplete}
        />
      )}
    </>
  );
}

export default EdgeDocs; 